// Third party dependencies
import 'bootstrap/dist/css/bootstrap.css';
import { UiProvider } from '@salecycle/ui-component-library';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';

// Local dependencies
import App from './core/ui/App';
import Authenticator from './core/ui/authenticator/authenticator';
import { AppProvider } from 'core/app/AppContext';
import { NotificationsProvider } from 'core/notifications/useNotificationsContext';
import { PreferencesProvider } from 'core/preferences/usePreferencesContext';
import { UserProvider } from 'core/users/UserContext';
import './i18n';
import { initStore } from './store';
import { configureAmplify } from './amplify';
import { hotjar } from 'react-hotjar';
import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk';

if (import.meta.env.VITE_ENV === 'production') {
  hotjar.initialize(1688374, 6);
}

configureAmplify();

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

export const store = initStore();

(async () => {
  const LDProvider = await asyncWithLDProvider({
    clientSideID: import.meta.env.VITE_LAUNCH_DARKLY_KEY,
    options: {
      sendEventsOnlyForVariation: true,
      /*
        Streaming is off for now as we don't need it and it doesn't work with 
        Cypress at the moment, but would be nice to add if it we can figure out
        how to support Cypress going forward (PD) 
      */
      streaming: false,
    },
  });
  ReactDOM.render(
    <LDProvider>
      <UiProvider>
        <QueryClientProvider client={queryClient}>
          <ReactQueryDevtools position={'bottom-right'} />
          <Provider store={store}>
            <AppProvider store={store}>
              <UserProvider>
                <PreferencesProvider>
                  <NotificationsProvider>
                    <Authenticator>
                      <App />
                    </Authenticator>
                  </NotificationsProvider>
                </PreferencesProvider>
              </UserProvider>
            </AppProvider>
          </Provider>
        </QueryClientProvider>
      </UiProvider>
    </LDProvider>,
    document.querySelector('#root'),
  );
})();
